/**
 * Shared value-formatting helpers — the renderer's "no raw JSON" rule.
 *
 * Bound values can be arbitrarily nested (objects, arrays of records, …).
 * Naive users must NEVER see braces/brackets, so every display component
 * funnels complex values through here (plain-text summaries) or through
 * <NestedValue> (rich inline rendering) in components/NestedValue.tsx.
 */

/** Scalars render directly; everything else needs structured treatment. */
export function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

/** Null / undefined / "" / [] / {} — rendered as a muted em-dash. */
export function isEmptyValue(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v as object).length === 0;
  return false;
}

/** Human form of a scalar: booleans read as Yes/No, numbers verbatim
 *  (no locale grouping — ids and references must survive untouched). */
export function formatScalar(v: string | number | boolean): string {
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

const ACRONYMS = new Set(["id", "url", "uri", "api", "ip", "sku", "qr", "sso", "otp"]);

/** snake_case / kebab-case / camelCase key → Title Case label.
 *  "pet_id" → "Pet ID", "createdAt" → "Created At". */
export function labelizeKey(key: string): string {
  return key
    .replace(/[_\-./]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map((w) => {
      const lower = w.toLowerCase();
      if (ACRONYMS.has(lower)) return lower.toUpperCase();
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ") || key;
}

/** Pick the most human field of a record for one-line summaries. */
const LABEL_KEYS = ["name", "title", "label", "display_name", "displayName",
  "email", "username", "subject", "status", "id"];

export function bestLabel(obj: Record<string, unknown>): string {
  for (const k of LABEL_KEYS) {
    const v = obj[k];
    if (isScalar(v) && String(v).trim() !== "") return formatScalar(v);
  }
  for (const v of Object.values(obj)) {
    if (isScalar(v) && String(v).trim() !== "") return formatScalar(v);
  }
  const n = Object.keys(obj).length;
  return `${n} ${n === 1 ? "field" : "fields"}`;
}

/**
 * Brace-free one-line summary of ANY value. Used wherever only a string fits
 * (tooltips, option labels, plain-text coercion in path.ts).
 */
export function summarizeValue(v: unknown): string {
  if (isEmptyValue(v)) return "—";
  if (isScalar(v)) return formatScalar(v);
  if (Array.isArray(v)) {
    if (v.every(isScalar)) {
      const head = v.slice(0, 4).map(formatScalar).join(", ");
      return v.length > 4 ? `${head} +${v.length - 4} more` : head;
    }
    const first = v[0];
    const firstLabel =
      first && typeof first === "object" && !Array.isArray(first)
        ? bestLabel(first as Record<string, unknown>)
        : summarizeValue(first);
    return `${v.length} ${v.length === 1 ? "item" : "items"} · ${firstLabel}`;
  }
  if (typeof v === "object") return bestLabel(v as Record<string, unknown>);
  return String(v);
}
